import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Typography,
} from '@mui/material';
import {
    AddShoppingCart as AddShoppingCartIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { toastr } from 'react-redux-toastr';

import { fetchStoreProduct } from '../../actions/catalogActions';
import { addToCart } from '../../actions/cartActions';
import CustomerLoginDialog from '../cart/CustomerLoginDialog';
import QuantityStepper from '../cart/QuantityStepper';
import PriceTag from '../storefront/PriceTag';
import ProductImage from '../storefront/ProductImage';
import { ProductOptions } from '../storefront/ProductDetail';
import {
    COLORS,
    FONT_BODY,
    FONT_TITLE,
    callButtonSx,
} from '../storefront/storefrontStyles';

// Opciones con un solo valor no son una elección: van preseleccionadas.
const initialSelection = (product) => {
    const selection = {};
    ((product && product.options) || []).forEach((option) => {
        const values = option.values || [];
        if (values.length === 1) selection[option.name] = values[0].value;
    });
    return selection;
};

// Comprar sin salir del vivo o de la llamada: un diálogo encima del video con
// lo justo para agregar al carrito (foto, precio, opciones, cantidad). No
// navega a ningún lado: salir de la pantalla cortaría la llamada. El carrito
// se paga después, desde "Mi carrito".
//
// `product` es lo que ya tenemos (un ítem del catálogo, o el pineado, que no
// trae opciones); el detalle completo se pide al abrir.
const ProductQuickDialog = ({
    open,
    onClose,
    storeId,
    product: initialProduct,
    origin,
    productState,
    customer,
    isAdding,
    fetchStoreProduct,
    addToCart,
}) => {
    const productId = initialProduct && initialProduct.productId;
    const [selectedOptions, setSelectedOptions] = useState({});
    const [quantity, setQuantity] = useState(1);
    const [loginOpen, setLoginOpen] = useState(false);

    useEffect(() => {
        if (open && productId) fetchStoreProduct(storeId, productId);
    }, [open, storeId, productId, fetchStoreProduct]);

    const notFound = !!productState && productState.notFound;
    const fullProduct = productState && productState.product;
    // el pineado llega sin opciones: hasta tener el detalle no se puede elegir
    const product =
        fullProduct ||
        (initialProduct && initialProduct.options ? initialProduct : null);

    useEffect(() => {
        if (open) {
            setSelectedOptions(initialSelection(product));
            setQuantity(1);
        }
        // sólo al abrir o al cambiar de producto
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, productId, !!product]);

    const options = (product && product.options) || [];
    const missingOption = options.find(
        (option) => !selectedOptions[option.name]
    );
    const canAdd = !!product && !missingOption && !isAdding && !notFound;

    const submitAdd = useCallback(async () => {
        const cart = await addToCart(storeId, {
            productId: Number(productId),
            quantity,
            selectedOptions,
            origin,
        });
        if (cart) {
            toastr.success(
                'Agregado al carrito',
                'Lo pagás desde "Mi carrito" cuando quieras.'
            );
            onClose();
        }
    }, [
        addToCart,
        storeId,
        productId,
        quantity,
        selectedOptions,
        origin,
        onClose,
    ]);

    const handleAdd = () => {
        if (!canAdd) return;
        if (!customer) {
            setLoginOpen(true);
            return;
        }
        submitAdd();
    };

    const handleLoggedIn = useCallback(() => {
        setLoginOpen(false);
        submitAdd();
    }, [submitAdd]);

    // lo que se muestra mientras llega el detalle
    const shown = product || initialProduct;
    const imageUrl =
        (shown &&
            ((shown.images && shown.images[0] && shown.images[0].url) ||
                shown.imageUrl)) ||
        null;

    return (
        <>
            <Dialog
                open={open && !loginOpen}
                onClose={onClose}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        backgroundColor: COLORS.surface,
                        color: COLORS.text,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '14px',
                        m: 1.5,
                    },
                }}
            >
                <IconButton
                    aria-label="Cerrar"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        color: 'white',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ p: 0 }}>
                    <ProductImage
                        src={imageUrl}
                        alt={shown ? shown.name : ''}
                        sx={{ paddingTop: '75%' }}
                    />
                    <Box
                        sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {notFound ? (
                            <Typography
                                sx={{
                                    color: COLORS.muted,
                                    fontFamily: FONT_BODY,
                                }}
                            >
                                Este producto ya no está disponible.
                            </Typography>
                        ) : (
                            <>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_TITLE,
                                            fontWeight: 800,
                                            fontSize: '1.5rem',
                                            lineHeight: 1.1,
                                            mb: 0.5,
                                        }}
                                    >
                                        {shown && shown.name}
                                    </Typography>
                                    {shown && (
                                        <PriceTag
                                            price={shown.price}
                                            compareAtPrice={
                                                shown.compareAtPrice
                                            }
                                            size="large"
                                        />
                                    )}
                                </Box>

                                {product ? (
                                    <ProductOptions
                                        options={product.options}
                                        selected={selectedOptions}
                                        onSelect={(name, value) =>
                                            setSelectedOptions((current) => ({
                                                ...current,
                                                [name]: value,
                                            }))
                                        }
                                        disabled={isAdding}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: COLORS.muted,
                                            fontFamily: FONT_BODY,
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        Cantidad:
                                    </Typography>
                                    <QuantityStepper
                                        value={quantity}
                                        onChange={setQuantity}
                                        disabled={isAdding}
                                    />
                                </Box>

                                <Box>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={
                                            isAdding ? (
                                                <CircularProgress
                                                    size={18}
                                                    sx={{
                                                        color:
                                                            COLORS.background,
                                                    }}
                                                />
                                            ) : (
                                                <AddShoppingCartIcon />
                                            )
                                        }
                                        disabled={!canAdd}
                                        onClick={handleAdd}
                                        sx={{
                                            ...callButtonSx,
                                            '&.Mui-disabled': {
                                                backgroundColor:
                                                    'rgba(255,255,255,0.12)',
                                                color: 'rgba(255,255,255,0.4)',
                                            },
                                        }}
                                    >
                                        Agregar al carrito
                                    </Button>
                                    {product && missingOption && (
                                        <Typography
                                            sx={{
                                                color: COLORS.muted,
                                                fontFamily: FONT_BODY,
                                                fontSize: '0.85rem',
                                                mt: 1,
                                            }}
                                        >
                                            Elegí {missingOption.name} para
                                            agregarlo al carrito.
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            <CustomerLoginDialog
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
                onLoggedIn={handleLoggedIn}
                title="Iniciá sesión para comprar"
                subtitle="Tu carrito queda guardado en tu cuenta. Usá Google o Apple, solo toma unos segundos."
            />
        </>
    );
};

const mapStateToProps = (state, ownProps) => {
    const productId = ownProps.product && ownProps.product.productId;
    return {
        productState: productId ? state.catalog.products[productId] : null,
        customer: state.customer.customer,
        isAdding: state.cart.isAdding,
    };
};

export default connect(mapStateToProps, { fetchStoreProduct, addToCart })(
    ProductQuickDialog
);
